import { COLORS } from "./constants";
import Icon from "./Icon";
import SketchColorPicker from "./SketchColorPicker";

const numInputStyle = {
  width: 80,
  padding: "10px 12px",
  fontSize: 15,
  border: "1.5px solid #d0d0d0",
  borderRadius: 8,
  outline: "none",
  textAlign: "center",
};

export default function SelEditorPopup({ obj, onUpdate, onCancel, onDone }) {
  const isSticker = obj.type === "sticker";
  const isMagnifier = obj.type === "magnifier";
  const canFill = obj.type === "rect" || obj.type === "circle" || obj.type === "line";
  const hasStroke = obj.type === "line" || obj.type === "arrow" || obj.type === "rect" || obj.type === "circle" || obj.type === "pen" || obj.type === "spotlight";
  const isText = obj.type === "text" || obj.type === "timestamp" || obj.text !== undefined || obj.labelFt !== undefined || obj.labelM !== undefined;
  const mode = obj.textMode || "abc";
  const isBold = obj.bold !== false;
  const align = obj.align || "center";
  const fontSize = obj.fontSize || 22;

  const getTitle = () => {
    switch (obj.type) {
      case "arrow": return "Edit Arrow";
      case "line": return "Edit Line";
      case "rect": return "Edit Rectangle";
      case "circle": return "Edit Circle";
      case "pen": return "Edit Pen";
      case "spotlight": return "Edit Spotlight";
      case "magnifier": return "Edit Magnifier";
      case "sticker": return "Edit Sticker";
      default: return "Edit Style";
    }
  };

  if (isSticker) {
    const size = obj.stickerSize || 40;
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}
        onMouseDown={onCancel}>
        <div onMouseDown={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,padding:24,width:320,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
          <h2 style={{fontSize:20,fontWeight:600,marginBottom:16,color:"#000"}}>{getTitle()}</h2>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <span style={{fontSize:12,color:"#555",width:40}}>Size</span>
            <input type="range" min={15} max={150} value={size}
              onChange={e => onUpdate({ stickerSize: Number(e.target.value) })}
              style={{flex:1,accentColor:"#007AFF"}}/>
            <span style={{fontSize:12,color:"#333",width:35,textAlign:"right"}}>{size}px</span>
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:4}}>
            <button onClick={onCancel} style={{padding:"10px 20px",borderRadius:8,border:"1px solid #d0d0d0",background:"transparent",fontSize:14,cursor:"pointer",color:"#333"}}>Cancel</button>
            <button onClick={onDone} style={{padding:"10px 24px",borderRadius:8,border:"none",background:"#007AFF",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>Save</button>
          </div>
        </div>
      </div>
    );
  }

  if (isMagnifier) {
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}
        onMouseDown={onCancel}>
        <div onMouseDown={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,padding:24,width:360,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
          <h2 style={{fontSize:20,fontWeight:600,marginBottom:16,color:"#000"}}>{getTitle()}</h2>

          <div style={{fontSize:12,color:"#555",marginBottom:8}}>Stroke Color</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:18}}>
            {COLORS.map(c => (
              <button key={c} onClick={() => onUpdate({ color: c })}
                style={{width:20,height:20,borderRadius:"50%",background:c,border:obj.color===c?"2.5px solid #007AFF":c==="#ffffff"?"1.5px solid #ccc":"1.5px solid transparent",cursor:"pointer"}}/>
            ))}
            <SketchColorPicker
              color={obj.color}
              size={20}
              title="Custom Stroke Color"
              onChange={c => onUpdate({ color: c })}
            />
          </div>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <span style={{fontSize:12,color:"#555",width:70}}>Stroke</span>
            <input type="range" min={1} max={12} value={obj.thickness || 5}
              onChange={e => onUpdate({ thickness: Number(e.target.value) })}
              style={{flex:1,accentColor:"#007AFF"}}/>
            <span style={{fontSize:12,color:"#333",width:30,textAlign:"right"}}>{obj.thickness || 5}px</span>
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:4}}>
            <button onClick={onCancel} style={{padding:"10px 20px",borderRadius:8,border:"1px solid #d0d0d0",background:"transparent",fontSize:14,cursor:"pointer",color:"#333"}}>Cancel</button>
            <button onClick={onDone} style={{padding:"10px 24px",borderRadius:8,border:"none",background:"#007AFF",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>Save</button>
          </div>
        </div>
      </div>
    );
  }

  function buildLabel(nextMode, raw) {
    const m = nextMode || mode;
    if (m === "abc") {
      if (raw?.abc !== undefined) return raw.abc;
      if (obj.labelAbc !== undefined) return obj.labelAbc;
      return (obj.textMode === "abc" || !obj.textMode) ? (obj.text ?? "") : "";
    }
    if (m === "ftin") return `${raw?.ft ?? obj.labelFt ?? 0} ft. ${raw?.in ?? obj.labelIn ?? 0} in.`;
    return `${raw?.m ?? obj.labelM ?? 0} m. ${raw?.cm ?? obj.labelCm ?? 0} cm.`;
  }

  function setMode(m) {
    const text = buildLabel(m);
    onUpdate({ textMode: m, text, lines: text ? text.split("\n") : undefined });
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}
      onMouseDown={onCancel}>
      <div onMouseDown={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,padding:24,width:380,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
        <h2 style={{fontSize:20,fontWeight:600,marginBottom:16,color:"#000"}}>{getTitle()}</h2>

        <div style={{display:"flex",border:"1px solid #d0d0d0",borderRadius:8,overflow:"hidden",marginBottom:16}}>
          {[["abc","Abc"],["ftin","ft. in."],["mcm","m. cm."]].map(([m,label])=>(
            <button key={m} onClick={()=>setMode(m)}
              style={{flex:1,padding:"9px 0",fontSize:13,fontWeight:500,background:mode===m?"#1c3a5c":"transparent",color:mode===m?"#fff":"#333",border:"none",cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>

        {mode==="abc" && (
          <input value={buildLabel("abc")} placeholder="Add a label" autoFocus
            onChange={e=>{ const text=e.target.value; onUpdate({ labelAbc: text, text, lines: text?text.split("\n"):undefined, textMode:"abc" }); }}
            style={{width:"100%",padding:"12px 14px",fontSize:16,border:"1.5px solid #d0d0d0",borderRadius:8,outline:"none",boxSizing:"border-box",marginBottom:16,fontWeight:isBold?"bold":"normal"}}/>
        )}
        {mode==="ftin" && (
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16}}>
            <input type="number" value={obj.labelFt||0}
              onChange={e=>{ const ft=e.target.value; onUpdate({ labelFt:ft, text:buildLabel("ftin",{ft}), lines:buildLabel("ftin",{ft}).split("\n"), textMode:"ftin" }); }}
              style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>ft.</span>
            <input type="number" value={obj.labelIn||0}
              onChange={e=>{ const inch=e.target.value; onUpdate({ labelIn:inch, text:buildLabel("ftin",{in:inch}), lines:buildLabel("ftin",{in:inch}).split("\n"), textMode:"ftin" }); }}
              style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>in.</span>
          </div>
        )}
        {mode==="mcm" && (
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16}}>
            <input type="number" value={obj.labelM||0}
              onChange={e=>{ const m=e.target.value; onUpdate({ labelM:m, text:buildLabel("mcm",{m}), lines:buildLabel("mcm",{m}).split("\n"), textMode:"mcm" }); }}
              style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>m.</span>
            <input type="number" value={obj.labelCm||0}
              onChange={e=>{ const cm=e.target.value; onUpdate({ labelCm:cm, text:buildLabel("mcm",{cm}), lines:buildLabel("mcm",{cm}).split("\n"), textMode:"mcm" }); }}
              style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>cm.</span>
          </div>
        )}

        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <span style={{fontSize:12,color:"#555",width:55}}>Text Size</span>
          <input type="range" min={10} max={80} value={fontSize}
            onChange={e => onUpdate({ fontSize: Number(e.target.value) })}
            style={{flex:1,accentColor:"#007AFF"}}/>
          <span style={{fontSize:12,color:"#333",width:45,textAlign:"right"}}>{Math.round(fontSize)}px</span>
        </div>

        <div style={{display:"flex",gap:6,marginBottom:16}}>
          <button onClick={() => onUpdate({ bold: !isBold })} title="Bold"
            style={{flex:1,padding:"8px 0",borderRadius:6,border:"1.5px solid #d0d0d0",background:isBold?"#1c1c3a":"#fff",color:isBold?"#fff":"#333",fontWeight:700,fontSize:14,cursor:"pointer"}}>B</button>
          {["left","center","right"].map(a => (
            <button key={a} onClick={() => onUpdate({ align: a })} title={a}
              style={{flex:1,padding:"8px 0",borderRadius:6,border:"1.5px solid #d0d0d0",background:align===a?"#1c1c3a":"#fff",color:align===a?"#fff":"#333",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon id={a==="left"?"alignleft":a==="center"?"aligncenter":"alignright"} size={14}/>
            </button>
          ))}
        </div>

        {isText && (
          <div style={{marginBottom:16}}>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#333",cursor:"pointer",marginBottom: obj.highlight ? 8 : 0}}>
              <input type="checkbox" checked={!!obj.highlight}
                onChange={e => {
                  const nextHl = e.target.checked;
                  const patch = { highlight: nextHl, highlightColor: obj.highlightColor || "#ffff00" };
                  if (nextHl && (obj.color === "#ffff00" || obj.color === "#ffcc00" || !obj.color)) {
                    patch.color = "#000000";
                  }
                  onUpdate(patch);
                }}/>
              <span style={{fontWeight: 600}}>Highlight Text</span>
            </label>
            {obj.highlight && (
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:6}}>
                {COLORS.map(c => (
                  <button key={c} onClick={() => onUpdate({ highlightColor: c })}
                    style={{width:20,height:20,borderRadius:"50%",background:c,border:(obj.highlightColor || "#ffff00")===c?"2.5px solid #007AFF":c==="#ffffff"?"1.5px solid #ccc":"1.5px solid transparent",cursor:"pointer"}}/>
                ))}
                <SketchColorPicker
                  color={obj.highlightColor || "#ffff00"}
                  size={20}
                  title="Custom Highlight Color"
                  onChange={c => onUpdate({ highlightColor: c })}
                />
              </div>
            )}
          </div>
        )}

        {hasStroke && (
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <span style={{fontSize:12,color:"#555",width:45}}>Stroke</span>
            <input type="range" min={1} max={20} value={obj.thickness || 5}
              onChange={e => onUpdate({ thickness: Number(e.target.value) })}
              style={{flex:1,accentColor:"#007AFF"}}/>
            <span style={{fontSize:12,color:"#333",width:30,textAlign:"right"}}>{obj.thickness || 5}px</span>
          </div>
        )}

        <div style={{fontSize:12,color:"#555",marginBottom:8}}>Color</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:canFill?16:20}}>
          {COLORS.map(c => (
            <button key={c} onClick={() => onUpdate({ color: c })}
              style={{width:20,height:20,borderRadius:"50%",background:c,border:obj.color===c?"2.5px solid #007AFF":c==="#ffffff"?"1.5px solid #ccc":"1.5px solid transparent",cursor:"pointer"}}/>
          ))}
          <SketchColorPicker
            color={obj.color}
            size={20}
            title="Custom Color"
            onChange={c => onUpdate({ color: c })}
          />
        </div>

        {canFill && (
          <div style={{marginBottom:20}}>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#333",marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={!!obj.filled}
                onChange={e => onUpdate({ filled: e.target.checked, fillColor: obj.fillColor || obj.color })}/>
              Fill shape
            </label>
            {obj.filled && (
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {COLORS.map(c => (
                  <button key={c} onClick={() => onUpdate({ fillColor: c })}
                    style={{width:20,height:20,borderRadius:"50%",background:c,border:obj.fillColor===c?"2.5px solid #007AFF":c==="#ffffff"?"1.5px solid #ccc":"1.5px solid transparent",cursor:"pointer"}}/>
                ))}
                <SketchColorPicker
                  color={obj.fillColor}
                  size={20}
                  title="Custom Fill Color"
                  onChange={c => onUpdate({ fillColor: c })}
                />
              </div>
            )}
          </div>
        )}

        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:4}}>
          <button onClick={onCancel} style={{padding:"10px 20px",borderRadius:8,border:"1px solid #d0d0d0",background:"transparent",fontSize:14,cursor:"pointer",color:"#333"}}>Cancel</button>
          <button onClick={onDone} style={{padding:"10px 24px",borderRadius:8,border:"none",background:"#007AFF",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>Save</button>
        </div>
      </div>
    </div>
  );
}
